/**
 * The tracked review link and the unsubscribe link (c133, c074): their public paths and the Google address they
 * lead to. Shared by `app` (which serves the paths), `jobs` (which puts them in emails) and `console`.
 *
 *   /g/<code>            the bare link: shared from the owner's phone or typed. A walk up.
 *   /g/<code>/c          the link the QR card carries. A walk up from the card.
 *   /g/<code>/<token>    the link in one ask's email: the tap is that ask's.
 *   /u/<ask>.<sig>       unsubscribe, signed for one ask (@ff/messaging signs and checks it).
 *
 * Every /g/ path answers with a redirect to the same Google review form, whoever taps it and whatever they think
 * of the job. There is no step in between and no way to add one: gating reviews breaks Google's policy and the FTC
 * rule (docs/43 §4).
 */
import { z } from 'zod';

/** A Google place ID as Google prints it, for example ChIJN1t_tDeuEmsRUsoyG83frY4. */
export const GooglePlaceId = z
  .string()
  .regex(/^[A-Za-z0-9_-]{10,300}$/, 'expected a Google place ID')
  .meta({ examples: ['ChIJN1t_tDeuEmsRUsoyG83frY4'] });

/** The review form for a place: the format Google's help gives for review links (docs/50 §4.4.2). */
export function googleReviewUrl(placeId: string): string {
  return `https://search.google.com/local/writereview?placeid=${encodeURIComponent(GooglePlaceId.parse(placeId))}`;
}

/** Lowercase letters and digits that can't be misread on a printed card: no 0, 1, l or o. */
export const LINK_ALPHABET = 'abcdefghijkmnpqrstuvwxyz23456789';
export const LINK_CODE_LENGTH = 8;
export const ASK_TOKEN_LENGTH = 16;

export const ReviewLinkCode = z.string().regex(/^[a-km-np-z2-9]{8}$/).meta({ examples: ['k3j9x2pq'] });
export const ReviewAskToken = z.string().regex(/^[a-km-np-z2-9]{16}$/).meta({ examples: ['k3j9x2pqk3j9x2pq'] });

function random(length: number): string {
  // 32 symbols, so a byte's low five bits pick one without bias.
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let out = '';
  for (const b of bytes) out += LINK_ALPHABET[b & 31];
  return out;
}

/** A new public code for a location's link (40 random bits: the link only ever leads to a public Google page). */
export const newReviewLinkCode = (): string => random(LINK_CODE_LENGTH);
/** A new token for one ask (80 random bits). */
export const newReviewAskToken = (): string => random(ASK_TOKEN_LENGTH);

export type ReviewLinkKind = 'link' | 'card' | 'ask';

/** The path for a link. `token` makes it an ask's link, `card` the QR card's. */
export function reviewLinkPath(code: string, opts: { token?: string | null; card?: boolean } = {}): string {
  ReviewLinkCode.parse(code);
  if (opts.token) return `/g/${code}/${ReviewAskToken.parse(opts.token)}`;
  return opts.card ? `/g/${code}/c` : `/g/${code}`;
}

/** Read a /g/ path. Null for anything else, so the caller answers 404 without a hint. */
export function parseReviewLinkPath(pathname: string): { code: string; kind: ReviewLinkKind; token: string | null } | null {
  const m = /^\/g\/([a-km-np-z2-9]{8})(?:\/(c|[a-km-np-z2-9]{16}))?\/?$/.exec(pathname);
  if (!m) return null;
  if (!m[2]) return { code: m[1]!, kind: 'link', token: null };
  if (m[2] === 'c') return { code: m[1]!, kind: 'card', token: null };
  return { code: m[1]!, kind: 'ask', token: m[2] };
}

/** `<review request id>.<signature>`: what an unsubscribe link carries. */
export const UnsubscribeToken = z.string().regex(/^rr_[a-z0-9]{6,40}\.[A-Za-z0-9_-]{43}$/);

export function unsubscribePath(token: string): string {
  return `/u/${UnsubscribeToken.parse(token)}`;
}

export function parseUnsubscribePath(pathname: string): string | null {
  const m = /^\/u\/(rr_[a-z0-9]{6,40}\.[A-Za-z0-9_-]{43})\/?$/.exec(pathname);
  return m ? m[1]! : null;
}
